import { useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { ContractRereadButton } from "./ContractRereadButton";

const PREVIEW = 8;
const TONE = {
  expired: { tab: "bg-red-600 text-white border-red-600", chip: "border-red-200 text-red-700", badge: "bg-red-100 text-red-700" },
  soon: { tab: "bg-amber-500 text-white border-amber-500", chip: "border-amber-200 text-amber-700", badge: "bg-amber-100 text-amber-800" },
};

const byDoc = (list) => {
  const m = {};
  list.forEach((u) => (m[u.document || "أخرى"] ||= []).push(u));
  return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
};

const DocGroup = ({ doc, list, kind, onPick, onGroup }) => {
  const [all, setAll] = useState(false);
  const shown = all ? list : list.slice(0, PREVIEW);
  const t = TONE[kind];
  return (
    <div className="rounded-xl border border-slate-100 bg-white/70 p-2.5" data-testid={`upcoming-group-${kind}-${doc}`}>
      <div className="mb-2 flex items-center gap-2">
      <button type="button" onClick={() => onGroup?.(doc, list)} title="عرض كل الموظفين في هذي الوثيقة" data-testid={`upcoming-group-open-${kind}-${doc}`}
        className="flex items-center gap-2 rounded-md text-sm font-bold text-slate-800 transition-colors hover:text-blue-700">
        {doc} <span className={`rounded-full px-2 py-0.5 text-[11px] ${t.badge}`}>{list.length}</span>
      </button>
      {kind === "expired" && doc === "العقد" && <ContractRereadButton />}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((u, i) => (
          <button key={i} type="button" onClick={() => onPick(u)} data-testid={`upcoming-chip-${kind}-${doc}-${i}`} title={u.attachment_id ? "فتح المستند" : "فتح ملف الموظف"}
            className={`rounded-full border bg-white px-2.5 py-0.5 text-xs transition-colors hover:bg-slate-50 ${t.chip}`}>
            {u.employee} · {u.days_left < 0 ? `منذ ${Math.abs(u.days_left)} ي` : `خلال ${u.days_left} ي`}
          </button>
        ))}
        {list.length > PREVIEW && (
          <button type="button" onClick={() => setAll((a) => !a)} className="px-1 text-xs font-bold text-slate-600 underline" data-testid={`upcoming-more-${kind}-${doc}`}>
            {all ? "عرض أقل" : `+${list.length - PREVIEW} المزيد`}
          </button>
        )}
      </div>
    </div>
  );
};

export const UpcomingAlerts = ({ items, onPick, onGroup }) => {
  const [open, setOpen] = useState(true);
  const expired = items.filter((u) => u.status === "expired").sort((a, b) => a.days_left - b.days_left);
  const soon = items.filter((u) => u.status !== "expired").sort((a, b) => a.days_left - b.days_left);
  const [kind, setKind] = useState(expired.length ? "expired" : "soon");
  const list = kind === "expired" ? expired : soon;
  const tabs = [["expired", "منتهية", expired.length], ["soon", "تقرب من الانتهاء", soon.length]];
  return (
    <div data-testid="upcoming-alerts" className="rounded-2xl border-l-4 border-l-amber-400 bg-amber-50/50 p-4">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="upcoming-toggle" className="flex w-full flex-wrap items-center gap-2 text-right">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="font-heading font-bold text-amber-800">وثائق تحتاج انتباهك ({items.length})</span>
        <span role="button" onClick={(ev) => { ev.stopPropagation(); setKind("expired"); setOpen(true); }} className="cursor-pointer rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700 hover:bg-red-200" data-testid="upcoming-expired-count">منتهية {expired.length}</span>
        <span role="button" onClick={(ev) => { ev.stopPropagation(); setKind("soon"); setOpen(true); }} className="cursor-pointer rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 hover:bg-amber-200" data-testid="upcoming-soon-count">تقرب {soon.length}</span>
        <ChevronDown className={`mr-auto h-4 w-4 text-amber-700 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {tabs.map(([k, label, n]) => (
              <button key={k} type="button" onClick={() => setKind(k)} data-testid={`upcoming-tab-${k}`}
                className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${kind === k ? TONE[k].tab : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {label} ({n})
              </button>
            ))}
          </div>
          {list.length ? (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {byDoc(list).map(([doc, l]) => <DocGroup key={`${kind}-${doc}`} doc={doc} list={l} kind={kind} onPick={onPick} onGroup={onGroup} />)}
            </div>
          ) : <p className="py-2 text-center text-xs text-slate-500" data-testid="upcoming-empty">لا توجد وثائق في هذا التصنيف</p>}
        </div>
      )}
    </div>
  );
};
